import React, { useEffect, useRef } from 'react';
import { TourProvider, useTour } from '@reactour/tour';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import SaleAttributionExample from './SaleAttributionExample';

const TOUR_KEY = 'chathub_onboarding_tour_v2';
// v2 / v5 (26/09) : tout le contenu des deux guides a ete reecrit — ordre du menu, quatre
// ecrans qui manquaient, vrais noms des filtres de la messagerie, et le meme tutoiement
// que le reste de l'application. Changer la cle rejoue le guide une fois pour tout le monde.
const INBOX_TOUR_KEY = 'chathub_inbox_tour_v5';

const STEP_KEYS = {
  // 26/09 : le tour suivait l'ordre d'ecriture du code, pas celui du menu, et sautait
  // quatre ecrans (Listes, Tracking, Abonnement, Affiliation). Il suit maintenant le
  // menu de haut en bas. Une etape peut exiger une option du plan (feature) : un client
  // Solo ne se fait pas expliquer un ecran qu'il n'a pas.
  main: [
    { id: 'welcome', selector: 'body', position: 'center', withTip: true },
    { id: 'accounts', selector: '[data-tour="nav-accounts"]', position: 'right' },
    { id: 'messages', selector: '[data-tour="nav-messages"]', position: 'right' },
    { id: 'lists', selector: '[data-tour="nav-lists"]', position: 'right' },
    { id: 'scripts', selector: '[data-tour="nav-scripts"]', position: 'right' },
    { id: 'vault', selector: '[data-tour="nav-vault"]', position: 'right' },
    { id: 'tracking', selector: '[data-tour="nav-tracking"]', position: 'right', feature: 'tracking_links' },
    { id: 'dropfans', selector: '[data-tour="nav-dropfans"]', position: 'right', steps: 4 },
    { id: 'chatters', selector: '[data-tour="nav-chatters"]', position: 'right' },
    { id: 'canal', selector: '[data-tour="nav-canal"]', position: 'right', feature: 'canal' },
    { id: 'whales', selector: '[data-tour="nav-whales"]', position: 'right', feature: 'whales' },
    { id: 'subscription', selector: '[data-tour="nav-subscription"]', position: 'right' },
    { id: 'affiliation', selector: '[data-tour="nav-affiliation"]', position: 'right' },
    { id: 'done', selector: 'body', position: 'center' },
  ],
  inbox: [
    { id: 'inboxWelcome', selector: 'body', position: 'center' },
    { id: 'accountTabs', selector: '[data-tour="inbox-accounts"]', position: 'bottom' },
    { id: 'convList', selector: '[data-tour="inbox-conv-list"]', position: 'right' },
    { id: 'seenWarning', selector: '[data-tour="inbox-conv-list"]', position: 'right' },
    { id: 'boxes', selector: '[data-tour="inbox-boxes"]', position: 'right' },
    // 26/09 : « toujours » = l'etape reste meme si sa cible n'est pas a l'ecran (elle est
    // alors centree). Le champ de saisie, les boutons et la fiche n'existent que si une
    // conversation est OUVERTE : sans ce drapeau, un chatteur qui lance le guide depuis la
    // liste perdait les 4 etapes les plus utiles (ecrire, scripts, Vault, traduction).
    { id: 'composer', selector: '[data-tour="inbox-composer"]', position: 'top', toujours: true },
    // 26/09 : identifiants PROPRES au guide de la messagerie. Avant, ils partageaient
    // « scripts » et « vault » avec le tour principal : le dernier texte ecrit ecrasait
    // l'autre, et le tour du menu expliquait un bouton de la messagerie.
    { id: 'inboxScripts', selector: '[data-tour="inbox-actions"]', position: 'top', toujours: true },
    { id: 'inboxVault', selector: '[data-tour="inbox-actions"]', position: 'top', toujours: true },
    { id: 'quickActions', selector: '[data-tour="inbox-actions"]', position: 'top', toujours: true },
    { id: 'sales', selector: '[data-tour="inbox-conv-list"]', position: 'right' },
    { id: 'aiAssistant', selector: '[data-tour="inbox-composer"]', position: 'top', feature: 'ai_assistant', toujours: true },
    { id: 'insightPanel', selector: '[data-tour="inbox-insight"]', position: 'left', toujours: true },
    { id: 'inboxClose', selector: 'body', position: 'center' },
  ],
};

const tourStyles = {
  popover: (base) => ({
    ...base,
    background: '#0B0B0E',
    color: '#e2e8f0',
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.1)',
    padding: 18,
    maxWidth: 'min(400px, 92vw)',
    // 26/09 (Marlon : « il y a des endroits ou ca partait hors champ ») : l'etape DropFans,
    // la seule avec une liste de 4 sous-etapes, descendait 73 px SOUS l'ecran en 1024x768 et
    // sa fin etait illisible. Corriger la POSITION par le DOM ne tient pas (React reecrit le
    // style inline aussitot). On borne donc la HAUTEUR : ancree au plus bas vers la moitie
    // de l'ecran, une carte de 42 % de la hauteur tient toujours (0,5 + 0,42 < 1), et un
    // contenu plus long defile a l'interieur de la carte.
    maxHeight: 'min(42vh, 420px)',
    overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
  }),
  maskWrapper: (base) => ({ ...base, color: 'rgba(2, 6, 23, 0.7)' }),
  badge: (base) => ({ ...base, background: '#6366f1', color: '#fff', fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap' }),
  controls: (base) => ({ ...base, marginTop: 12 }),
  close: (base) => ({ ...base, color: '#94a3b8', top: 12, right: 12 }),
};

function TourContent({ tour, forceOpen, autoOuvrir = true, ouvertRef }) {
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  const { t } = useTranslation();
  const { hasFeature } = useAuth();
  const storageKey = tour === 'inbox' ? INBOX_TOUR_KEY : TOUR_KEY;

  useEffect(() => {
    let annule = false;
    // 26/09 : le guide suit le plan. Une etape qui explique une option absente du plan est
    // retiree, et une etape dont la cible n'est pas a l'ecran est sautee plutot que de casser
    // le tour sur un selecteur introuvable.
    // ⚠ Ce tri se faisait AU RENDU, donc AVANT que le menu de gauche existe : au tout premier
    // lancement (nouveau client), tout etait filtre et il ne restait que 2 etapes sur 14.
    // On attend donc que la premiere cible soit dans la page — 2 s au maximum.
    // « Prete » = AU MOINS UNE cible de CE guide est dans la page. On ne teste pas une
    // cible precise : selon le role, tel ecran peut ne jamais exister (un chatteur n'a pas
    // « Comptes »), et on attendrait pour rien. Le menu de gauche ne convient pas non plus
    // comme temoin : l'ecran Messagerie n'en a pas du tout.
    const cibles = (STEP_KEYS[tour] || STEP_KEYS.main)
      .filter((s) => s.selector !== 'body')
      .map((s) => s.selector);
    const prete = () => typeof document === 'undefined'
      || cibles.some((sel) => !!document.querySelector(sel));

    const construire = () => {
    // Sur téléphone, les éléments ciblés (menu, panneaux) sont dans un tiroir ou masqués →
    // on centre chaque étape (carte au milieu de l'écran) avec le même texte, sans ancrage.
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const stepKeys = (STEP_KEYS[tour] || STEP_KEYS.main).filter((s) => {
      if (s.feature && hasFeature && !hasFeature(s.feature)) return false;
      if (s.toujours || s.selector === 'body' || typeof document === 'undefined') return true;
      return !!document.querySelector(s.selector);
    });
    // 26/09 (Marlon, capture) : « center » ne centre PAS la carte — la bibliotheque pose son
    // COIN GAUCHE au milieu de l'ecran. Mesure en 520x700 : carte de 400 px posee a x=260,
    // donc 140 px hors ecran a droite, sur les 14 etapes. On calcule nous-memes le centre.
    const L = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const H = typeof window !== 'undefined' ? window.innerHeight : 768;
    const largeurCarte = Math.min(400, L * 0.92);
    const hauteurCarte = Math.min(H * 0.42, 420);
    const centre = [Math.max(12, (L - largeurCarte) / 2), Math.max(12, (H - hauteurCarte) / 2)];
    // 29/09 : les titres portent un numero ecrit en dur (« 8. Ton equipe »), celui du tour
    // COMPLET. Des qu'un role a un menu reduit, des etapes disparaissent et la suite saute :
    // un manager lisait 1,2,3,4,5,6,8,10 pendant que le badge affichait 7/9. On renumerote
    // donc sur la liste reellement affichee. Les etapes sans numero (accueil, fin) n'en
    // recoivent pas. Marche aussi en anglais : le motif est « <chiffres>. ».
    let numero = 0;
    const steps = stepKeys.map((s) => {
      const absente = s.selector !== 'body' && typeof document !== 'undefined'
        && !document.querySelector(s.selector);
      const centree = isMobile || absente || s.position === 'center';
      const brut = t(`tour.${s.id}.title`);
      const estNumerote = /^\d+\.\s/.test(brut);
      if (estNumerote) numero += 1;
      const titre = estNumerote ? brut.replace(/^\d+\.\s*/, `${numero}. `) : brut;
      return {
      selector: centree ? 'body' : s.selector,
      position: centree ? centre : s.position,
      content: (
        <div className="text-sm leading-relaxed space-y-2">
          <div className="text-white font-semibold text-base mb-1">{titre}</div>
          <div className="text-slate-300">{t(`tour.${s.id}.content`)}</div>
          {s.id === 'sales' && <div className="pt-1"><SaleAttributionExample compact /></div>}
          {s.steps && (
            <ol className="list-decimal pl-4 space-y-1.5 text-slate-300 text-[13px]">
              {Array.from({ length: s.steps }, (_, i) => (
                <li key={i}>{t(`tour.${s.id}.step${i + 1}`)}</li>
              ))}
            </ol>
          )}
          {s.withTip && (
            <div className="text-slate-400 text-xs pt-2 border-t border-white/5">
              {t('tour.welcome.tip')}{' '}
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white border border-white/20 font-mono text-[10px]">Ctrl</kbd>{' + '}
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white border border-white/20 font-mono text-[10px]">-</kbd>{' / '}
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white border border-white/20 font-mono text-[10px]">Ctrl 0</kbd>
            </div>
          )}
        </div>
      ),
      }; });
      if (typeof setSteps === 'function') setSteps(steps);
      return steps.length;
    };

    let essais = 0;
    const tenter = () => {
      if (annule) return;
      if (!prete() && essais < 50) { essais += 1; setTimeout(tenter, 100); return; }   // 5 s au maximum
      // Aucune cible de ce guide sur l'ecran courant (ex. le guide du tableau de bord alors
      // qu'on est dans la Messagerie, qui n'a pas de menu) : on n'ouvre pas une coquille vide
      // et on ne marque rien comme vu — il se lancera sur le bon ecran.
      if (!prete() && !forceOpen) return;
      construire();
      const seen = localStorage.getItem(storageKey);
      // forceOpen = relecture demandee (le bouton « Guide » nous remonte). On ouvre meme si
      // le drapeau « deja vu » est pose : le demontage de l'instance precedente le repose.
      // 29/09 : autoOuvrir=false => ce role ne recoit pas le guide tout seul (comportement
      // inchange), mais le bouton « Guide » du Profil peut quand meme le lui ouvrir.
      if (forceOpen || (!seen && autoOuvrir)) {
        setCurrentStep(0);
        setIsOpen(true);
        ouvertRef.current = true;
      }
    };
    setTimeout(tenter, 100);
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, t, forceOpen, autoOuvrir]);

  // 26/09 (Marlon : « il y a des endroits ou ca partait hors champ, on ne pouvait pas bien
  // le lire ») : la bibliotheque place la carte par « transform: translate(x, y) » SANS
  // verifier qu'elle tient dans la fenetre. Mesure en production, en 1024x768 : l'etape
  // DropFans (la seule avec 4 sous-etapes, 457 px de haut) etait posee a y=384 et finissait
  // donc 73 px SOUS le bas de l'ecran. Une hauteur maximale ne suffit pas : c'est la
  // POSITION qu'il faut corriger. On remet la carte dans la fenetre apres chaque placement.
  // 26/09 : on ne marque « deja vu » QUE si le guide s'est reellement ouvert. Avant, le
  // simple fait de quitter l'ecran posait le drapeau : un client qui passait deux secondes
  // sur la Messagerie n'avait plus jamais son guide, sans l'avoir vu une seule fois.
  useEffect(() => () => {
    if (ouvertRef.current) localStorage.setItem(storageKey, '1');
  }, [storageKey]);

  return null;
}

function PrevButton({ currentStep, setCurrentStep, t }) {
  if (currentStep <= 0) return null;
  return (
    <button onClick={() => setCurrentStep(currentStep - 1)} className="text-slate-400 hover:text-white text-xs font-medium px-3 py-1.5">
      ← {t('tour.nav.back')}
    </button>
  );
}

function NextButton({ currentStep, setCurrentStep, stepsLength, setIsOpen, storageKey, t }) {
  const last = currentStep === stepsLength - 1;
  return (
    <button
      data-testid="tour-next"
      onClick={() => {
        if (last) { localStorage.setItem(storageKey, '1'); setIsOpen(false); }
        else setCurrentStep(currentStep + 1);
      }}
      className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold rounded-lg px-4 py-2 transition-colors shadow-md shadow-indigo-500/30"
    >
      {last ? t('tour.nav.last') : t('tour.nav.next')} →
    </button>
  );
}

export default function OnboardingTour({ tour = 'main', forceOpen = false, autoOuvrir = true }) {
  const storageKey = tour === 'inbox' ? INBOX_TOUR_KEY : TOUR_KEY;
  const { t } = useTranslation();
  // Vrai seulement une fois le guide REELLEMENT affiche : sans ca, le simple montage du
  // fournisseur marquait le guide comme vu et le client ne le voyait jamais.
  const ouvertRef = useRef(false);

  return (
    <TourProvider
      steps={[]}
      styles={tourStyles}
      padding={{ mask: 8, popover: 16 }}
      showCloseButton
      showBadge
      showNavigation
      // 26/09 (Marlon, capture a l'appui) : avec 14 etapes, les 14 pastilles de progression
      // s'entassaient ENTRE « Retour » et « Suivant » dans 362 px : la barre passait sur deux
      // lignes et « Retour » se retrouvait coupe. Mesure : barre de 48 px de haut au lieu de 24.
      // On retire les pastilles — l'avancement est desormais dans la pastille du coin (« 11 / 14 »).
      showDots={false}
      badgeContent={({ currentStep, totalSteps }) => `${currentStep + 1} / ${totalSteps}`}
      disableInteraction={false}
      onClickMask={() => { /* prevent close on mask click */ }}
      afterOpen={() => { ouvertRef.current = true; document.body.style.overflow = 'hidden'; }}
      beforeClose={() => {
        if (ouvertRef.current) localStorage.setItem(storageKey, '1');
        document.body.style.overflow = '';
      }}
      // eslint-disable-next-line react/no-unstable-nested-components
      prevButton={(props) => <PrevButton {...props} t={t} />}
      // eslint-disable-next-line react/no-unstable-nested-components
      nextButton={(props) => <NextButton {...props} storageKey={storageKey} t={t} />}
    >
      <TourContent tour={tour} forceOpen={forceOpen} autoOuvrir={autoOuvrir} ouvertRef={ouvertRef} />
    </TourProvider>
  );
}

// 26/09 (Marlon : « quand je mets guide reinitialiser, ca ne me relance pas ») : l'ecran
// Profil effacait « chathub_onboarding_tour_v1 » dans sessionStorage, alors que le drapeau
// vit dans localStorage sous « ..._v2 » (et « ..._v5 » pour la messagerie). Il n'effacait
// donc RIEN. Ces deux fonctions sont la seule facon correcte de relancer un guide.
// 29/09 (Marlon : « meme quand on clique sur ce bouton, ca ne le relance pas ») :
// effacer le drapeau ne suffit pas. <OnboardingTour> vit dans DashboardLayout, qui ne se
// remonte PAS quand on change de page : son effet d'ouverture ne rejoue jamais. Il faut
// donc demander explicitement la remontee, une fois la navigation terminee.
export const EVT_RELANCE_GUIDE = 'chathub:relancer-guide';

export const demanderRelanceGuide = (quoi = 'main') => {
  try { window.dispatchEvent(new CustomEvent(EVT_RELANCE_GUIDE, { detail: quoi })); } catch (e) { /* SSR / vieux navigateur */ }
};

export const relancerGuidePrincipal = () => {
  localStorage.removeItem(TOUR_KEY);
  sessionStorage.removeItem(TOUR_KEY);
};

export const relancerGuideMessagerie = () => {
  localStorage.removeItem(INBOX_TOUR_KEY);
  sessionStorage.removeItem(INBOX_TOUR_KEY);
};

export const resetOnboardingTour = () => {
  localStorage.removeItem(TOUR_KEY);
  localStorage.removeItem(INBOX_TOUR_KEY);
  // Also clear any old sessionStorage flag from legacy sessions
  sessionStorage.removeItem(TOUR_KEY);
  sessionStorage.removeItem(INBOX_TOUR_KEY);
};

// Clears just the Messaging/inbox tour so it can be replayed on the spot
// (the caller remounts <OnboardingTour tour="inbox" /> via a key bump).
export const replayInboxTour = () => {
  localStorage.removeItem(INBOX_TOUR_KEY);
};
